import React, { useCallback, useEffect, useState } from 'react'
import { omitBy, isUndefined } from '@golden/utils'
import { Branch, FinanceCourierQuery } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import EditFinanceCourierForm, {
  EditFinanceCourierErrorType,
  EditFinanceCourierFormType,
  initialError,
  initialForm
} from '../../../components/admin/finance/EditFinanceCourierForm'
import useT from '../../../i18ns/admin/useT'
import { useDialogHandleSubmit, useRedirectHandleBack, useGetDataByParams } from '../../../utils/default/ComplexFlowHook'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { Path } from '../../../components/admin/route/route'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'

const formToRequest: (form: EditFinanceCourierFormType) => FinanceCourierQuery = (form) => {
  const converted = {
    name: form.name,
    slug: form.slug,
    secret: Object.entries(form.secret).some(([_key, value]) => !!value) ? form.secret : undefined,
    is_active: form.is_active
  } as FinanceCourierQuery
  return omitBy(converted, isUndefined) as FinanceCourierQuery
}

const FinanceCourierUpdatePage: React.FC = () => {
  const classes = useCommonStyles()
  const globalDialog = useGlobalDialog()
  const pageFlow = usePageFlow()
  const [id, setId] = useState<number>(0)
  const [formError] = useState<EditFinanceCourierErrorType>(initialError())
  const [defaultForm, setDefaultForm] = useState<EditFinanceCourierFormType>(initialForm())
  const gdk = useGDK()
  const { t } = useT()
  const forwarders = useGDKStore.finance.forwarders()

  useEffect(() => {
    gdk.trunk.trigger([Branch.FORWARDERS])
  }, [gdk])

  useGetDataByParams({
    path: Path.FINANCE_COURIER_UPDATE,
    gdkFunc: (param: { id: string }) => gdk.finance.getCourier(Number(param.id)),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res) => {
      setId(res.id)
      setDefaultForm(initialForm(res))
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: id === 0
  })

  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({
    path: Path.FINANCE_COURIER
  })

  const { handleSubmit } = useDialogHandleSubmit<EditFinanceCourierFormType, FinanceCourierQuery, null>({
    dialogId: 'updateCourier',
    globalDialog,
    getChangeDialogConfig: useCallback((form) => createGlobalDialogConfig({
      showIcon: false,
      message: (
        <React.Fragment>
          {t('dialog.confirmUpdateCourier')}
          <span className={classes.purpleWord}>{`${forwarders[form.slug]?.name ?? ''} ${form.name}`}</span>
        </React.Fragment>
      )
    }), [t, forwarders, classes.purpleWord]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.updateCourierSuccess')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    formToRequest,
    gdkFunc: (payload) => gdk.finance.updateCourier(id, payload),
    gdkFuncDependencies: [gdk, id],
    afterSuccessDialog: handleBack
  })

  return (
    <Box paddingY={5}>
      <LoadingAndErrorFrame { ...pageFlow.status } >
        <EditFinanceCourierForm
          title={t('common.updateCourier')}
          okText={t('common.confirmUpdate')}
          onBack={handleDebouncedBack}
          onSubmit={handleSubmit}
          defaultValue={defaultForm}
          disableForwarder
          formError={formError}
        />
      </LoadingAndErrorFrame>
    </Box>
  )
}

export default React.memo(FinanceCourierUpdatePage)
